// DTOs de C2 — programación semanal de líderes (coordinador).
//
// Refactor 2026-05-26: visual alineado al diseño piloto UPG.
//   - GET /api/coordinador/programacion-lideres?semana_iso=YYYY-Www
//     Response extendido: lideres_enriquecidos, conflictos, metricas
//   - POST /api/coordinador/programacion-lideres
//   - PATCH /api/coordinador/programacion-lideres/:id (mover programación, drag-drop)
//   - DELETE /api/coordinador/programacion-lideres/:id
//   - POST /api/coordinador/programacion-lideres/copiar-semana
//
// Scope: coordinador solo programa para líneas en plantas asignadas en
// `usuarios_coordinador_plantas`. Backend valida.
import { z } from 'zod';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
// =============================================================================
// Recursos del scope (líderes, líneas, turnos) para poblar selects/grid
// =============================================================================
const liderResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
});
const turnoResumenSchema = z.object({
    id: z.string().uuid(),
    // BD permite 1-4 (CHECK constraint). T4 es opcional en plantas con turno extra.
    numero: z.number().int().min(1).max(4),
    nombre: z.string(),
    hora_inicio: z.string().regex(horaTimeRegex),
    hora_fin: z.string().regex(horaTimeRegex),
    cruza_medianoche: z.boolean(),
});
// =============================================================================
// Horario resuelto por (turno, fecha) de la semana — design horario-turno-dia
// §5: el FE lo usa para deshabilitar celdas de días "No opera" (S5b).
// =============================================================================
export const horarioPorFechaLideresSchema = z.object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    no_opera: z.boolean(),
    hora_inicio: z.string().regex(horaTimeRegex).nullable(),
    hora_fin: z.string().regex(horaTimeRegex).nullable(),
    cruza_medianoche: z.boolean(),
});
// =============================================================================
// Programación individual (enriquecida con nombres para el grid)
// =============================================================================
export const programacionLiderEnriquecidaSchema = z.object({
    id: z.string().uuid(),
    semana_iso: z.string().regex(semanaIsoRegex),
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    lider_user_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    es_linea_principal: z.boolean(),
    // Nombres derivados (para el grid)
    lider_nombre: z.string(),
    lider_apellido: z.string(),
});
// =============================================================================
// Líder enriquecido para el panel "Líderes disponibles" del diseño
// =============================================================================
export const liderEnriquecidoSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    /** "DC" para Daniela Cortés, "MF" para Maín Felipe, etc. */
    iniciales: z.string().min(1).max(4),
    /** Planta donde el líder tiene mayoría de asignaciones en la semana actual.
     *  null si no tiene asignaciones esta semana o trabajo cross-plantas equitativo. */
    planta_principal_id: z.string().uuid().nullable(),
    planta_principal_nombre: z.string().nullable(),
    /** Cantidad de celdas (fecha,turno,linea) asignadas en la semana. */
    asignaciones_count: z.number().int().min(0),
    /** Suma de horas de turno asignadas en la semana (usa duración real del turno). */
    horas_asignadas: z.number().min(0),
});
// =============================================================================
// Conflicto: mismo líder en 2+ celdas de un mismo (fecha, turno)
// =============================================================================
export const conflictoProgramacionSchema = z.object({
    /** id sintético para keys en React: `${fecha}|${turno_id}|${lider_user_id}` */
    id: z.string(),
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    turno_numero: z.number().int().min(1).max(4),
    lider_user_id: z.string().uuid(),
    lider_nombre: z.string(),
    lider_apellido: z.string(),
    /** Líneas en las que el líder está doblemente asignado (≥2 entradas). */
    lineas: z
        .array(z.object({
        linea_id: z.string().uuid(),
        linea_codigo: z.string(),
        linea_nombre: z.string(),
    }))
        .min(2),
});
// =============================================================================
// Métricas del header en tiempo real
// =============================================================================
export const programacionMetricasSchema = z.object({
    /** Celdas (fecha,turno,linea) con líder asignado. */
    celdas_asignadas: z.number().int().min(0),
    /** Total de celdas en la semana = lineas × turnos × 7. */
    celdas_total: z.number().int().min(0),
    /** Líneas con TODAS sus celdas asignadas (7 días × N turnos). */
    lineas_completas_count: z.number().int().min(0),
    lineas_total: z.number().int().min(0),
    conflictos_count: z.number().int().min(0),
});
// =============================================================================
// Response GET /programacion-lideres?semana_iso=...
// =============================================================================
export const coordinadorProgramacionSemanaSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    }),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Fechas reales de la semana (lunes a domingo) en YYYY-MM-DD. */
    dias: z.array(z.string().date()).length(7),
    fecha_hoy: z.string().date(),
    /** Recursos del scope para selects/modal (estructura básica). */
    lideres: z.array(liderResumenSchema),
    lineas: z.array(lineaResumenSchema),
    turnos: z.array(turnoResumenSchema),
    /** Filas ya creadas para esa semana. */
    programaciones: z.array(programacionLiderEnriquecidaSchema),
    /** Líderes con metadata útil para el panel "Líderes disponibles" del diseño. */
    lideres_enriquecidos: z.array(liderEnriquecidoSchema),
    /** Conflictos calculados server-side (consistente con métricas). */
    conflictos: z.array(conflictoProgramacionSchema),
    /** KPIs del header. */
    metricas: programacionMetricasSchema,
    /** Existe programación en la semana anterior (para habilitar "Copiar semana anterior"). */
    semana_anterior_tiene_data: z.boolean(),
    /** Horario resuelto por (turno, fecha) de la semana — para deshabilitar celdas "No opera" (design §5). */
    horarios_por_fecha: z.array(horarioPorFechaLideresSchema),
});
// =============================================================================
// POST input
// =============================================================================
export const programacionLiderCreateSchema = z.object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    lider_user_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    es_linea_principal: z.boolean().default(true),
});
// =============================================================================
// PATCH input — mover programación (drag-drop)
// =============================================================================
//
// Permite cambiar fecha, turno, línea y/o líder. Como el UNIQUE constraint
// incluye (tenant_id, fecha, turno_id, lider_user_id, linea_id), el handler
// debe rechazar 409 si el nuevo destino ya existe.
export const programacionLiderPatchSchema = z
    .object({
    fecha: z.string().date().optional(),
    turno_id: z.string().uuid().optional(),
    lider_user_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
    es_linea_principal: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'al menos un campo requerido',
});
// =============================================================================
// POST /copiar-semana input + response
// =============================================================================
//
// Copia TODAS las programaciones de la semana origen a la semana destino,
// ajustando fechas (mismo offset de lunes-a-lunes). Rechaza si la semana
// destino YA tiene programaciones (para evitar duplicar accidentalmente).
export const copiarSemanaInputSchema = z.object({
    semana_iso_destino: z.string().regex(semanaIsoRegex),
    /** Opcional: si null, usa la semana inmediatamente anterior a la destino. */
    semana_iso_origen: z.string().regex(semanaIsoRegex).optional(),
});
export const copiarSemanaResponseSchema = z.object({
    filas_creadas: z.number().int().min(0),
    semana_iso_origen: z.string().regex(semanaIsoRegex),
    semana_iso_destino: z.string().regex(semanaIsoRegex),
    /** Asignaciones de origen omitidas porque el destino cae en un día "No opera" (design §5). */
    omitidas_dia_sin_turno: z.number().int().min(0),
});
//# sourceMappingURL=coordinador_programacion_lideres.js.map